/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Button, Input, Form, Divider, PageHeader, notification } from 'antd';
import axios from 'axios';
import { useTranslation, withTranslation } from 'react-i18next';
import ConfigContext from '../../../../../../../../../../components/ConfigContext';
import { handleApiError } from '../../../../../../../../../../services/utils/errorHandler';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 14 },
  },
};

const buttonItemLayout = {
  wrapperCol: { span: 24, offset: 19 },
};

const VppCredentials = props => {
  const { config } = useContext(ConfigContext);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const formRef = useRef(null);

  useEffect(() => {
    props.getIosUserData(props.user);
  }, []);

  const onFinish = values => {
    props.callback();
    values.dmUsername = props.user;
    if (values.hasOwnProperty('inviteCode')) {
      updateIosUser(values);
    } else {
      createIosUser(values);
    }
  };

  const createIosUser = values => {
    let apiURL =
      window.location.origin +
      config.serverConfig.invoker.uri +
      config.serverConfig.invoker.store +
      '/vpp/user';

    axios
      .post(apiURL, values, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(res => {
        notification.success({
          message: 'Done',
          duration: 2,
          description: t('api_createVppCredentials'),
        });
      })
      .catch(error => {
        handleApiError(error, t('api_createVppCredentialsError'), t);
        setLoading(false);
      });
  };

  const updateIosUser = values => {
    let apiURL =
      window.location.origin +
      config.serverConfig.invoker.uri +
      config.serverConfig.invoker.store +
      '/vpp/user';

    axios
      .put(apiURL, values, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(res => {
        notification.success({
          message: 'Done',
          duration: 2,
          description: t('api_updateVppCredentials'),
        });
      })
      .catch(error => {
        handleApiError(error, t('api_updateVppCredentialsError'), t);
        setLoading(false);
      });
  };

  return (
    <div>
      <Divider>
        <PageHeader
          className="site-page-header-responsive"
          subTitle={t('title_createNewIosUser')}
        />
      </Divider>
      {props.vppData && (
        <Form
          {...formItemLayout}
          name="addIoSUser"
          onFinish={onFinish}
          ref={formRef}
          initialValues={{
            clientUserId: props.vppData.clientUserId,
            email: props.vppData.email,
            managedId: props.vppData.managedId,
            tmpPassword: props.vppData.tmpPassword,
          }}
        >
          <Form.Item
            name="clientUserId"
            label={t('label_clientUserId')}
            rules={[
              {
                required: true,
                message: t('form_clientIdRequiredMsg'),
              },
            ]}
          >
            <Input placeholder={t('label_clientUserId')} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('label_email')}
            rules={[
              {
                type: 'email',
                message: t('form_emailNotValidMsg'),
              },
              {
                required: true,
                message: t('form_emailRequiredMsg'),
              },
            ]}
          >
            <Input placeholder={t('label_email')} />
          </Form.Item>
          <Form.Item
            name="managedId"
            label={t('label_AppleId')}
            rules={[
              {
                required: true,
                message: t('form_managedIdRequiredMsg'),
              },
            ]}
          >
            <Input placeholder={t('label_AppleId')} />
          </Form.Item>
          <Form.Item
            name="tmpPassword"
            label={t('label_ApplePassword')}
            rules={[
              {
                required: true,
                message: t('form_tempPasswordRequiredMsg'),
              },
              {
                pattern: new RegExp('^(((?!(\\s)).){5,})*$'),
                message: t('form_passwordNotValidMsg'),
              },
            ]}
          >
            <Input.Password
              placeholder={t('label_AppleIdPassword')}
              iconRender={visible =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>
          {props.vppData.inviteCode ? (
            <Form.Item name="inviteCode" label={t('label_inviteCode')}>
              <Input disabled defaultValue={props.vppData.inviteCode} />
            </Form.Item>
          ) : null}
          {props.vppData.status ? (
            <Form.Item name="status" label={t('label_status')}>
              <Input disabled defaultValue={props.vppData.status} />
            </Form.Item>
          ) : null}
          {props.vppData.createdTime ? (
            <Form.Item name="createdTime" label={t('label_createdTime')}>
              <Input disabled defaultValue={props.vppData.createdTime} />
            </Form.Item>
          ) : null}
          <Form.Item {...buttonItemLayout}>
            <Button loading={loading} type="primary" htmlType="submit">
              {t('label_submit')}
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default withTranslation()(VppCredentials);
